// Qual aplicativo de navegação recebe o clique — e como montar o link de cada um.
//
// Existe porque os links da rota de cobrança eram todos `google.com/maps`, e no
// iPhone sem o app do Google Maps instalado isso cai no Safari: para um destino
// só a web até resolve, mas com `waypoints` ela exige login e a rota não abre.
// O gestor que vai a campo usa iPhone, então o link precisava de escolha.
//
// A escolha NÃO pode ser automática pelo aparelho de quem gera a rota: quem monta
// costuma estar no desktop do escritório e quem dirige está no celular. Por isso a
// preferência é explícita e fica salva; a detecção só define o palpite inicial.
//
// Limite que muda o desenho da tela: o esquema de URL do Apple Maps aceita um
// `saddr` e um `daddr` e mais nada — não existe parâmetro de parada intermediária
// (documentação da Apple, "Map Links"). Rota encadeada de 10 paradas num link só
// é exclusividade do Google Maps; no Apple e no Waze a navegação é parada a
// parada. Ver `suportaRotaEncadeada`.

export type AppNavegacao = 'google' | 'apple' | 'waze';

export const APPS_NAVEGACAO: Array<{ valor: AppNavegacao; rotulo: string }> = [
    { valor: 'google', rotulo: 'Google Maps' },
    { valor: 'apple', rotulo: 'Apple Maps' },
    { valor: 'waze', rotulo: 'Waze' },
];

/** Só o Google Maps aceita paradas intermediárias por URL. */
export const suportaRotaEncadeada = (app: AppNavegacao): boolean => app === 'google';

const CHAVE_PREFERENCIA = 'vigorhub:app-navegacao';

/**
 * iPhone/iPad. O iPad moderno se apresenta como Mac no user agent, daí o segundo
 * teste — um Mac de verdade não tem tela sensível ao toque.
 */
const ehIOS = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1;
};

const ehValido = (v: unknown): v is AppNavegacao =>
    v === 'google' || v === 'apple' || v === 'waze';

/** A preferência salva ou, na falta dela, o palpite pelo aparelho. */
export const appNavegacaoPreferido = (): AppNavegacao => {
    try {
        const salvo = localStorage.getItem(CHAVE_PREFERENCIA);
        if (ehValido(salvo)) return salvo;
    } catch { /* modo privativo ou storage bloqueado */ }
    return ehIOS() ? 'apple' : 'google';
};

export const salvarAppNavegacao = (app: AppNavegacao): void => {
    try { localStorage.setItem(CHAVE_PREFERENCIA, app); } catch { /* idem */ }
};

/** Navegação até uma coordenada, já em modo direção de carro. */
export const navUrlCoordenada = (
    p: { lat: number; lng: number },
    app: AppNavegacao = 'google',
): string => {
    const alvo = `${p.lat},${p.lng}`;
    if (app === 'apple') return `https://maps.apple.com/?daddr=${alvo}&dirflg=d`;
    // `navigate=yes` é o que faz o Waze já iniciar a rota em vez de só marcar o pino.
    if (app === 'waze') return `https://waze.com/ul?ll=${alvo}&navigate=yes`;
    return `https://www.google.com/maps/dir/?api=1&destination=${alvo}&travelmode=driving`;
};

/** Navegação para um endereço em TEXTO — quando a coordenada não é do endereço. */
export const navUrlEndereco = (
    endereco: string,
    app: AppNavegacao = 'google',
): string => {
    const alvo = encodeURIComponent(endereco);
    if (app === 'apple') return `https://maps.apple.com/?daddr=${alvo}&dirflg=d`;
    if (app === 'waze') return `https://waze.com/ul?q=${alvo}&navigate=yes`;
    return `https://www.google.com/maps/dir/?api=1&destination=${alvo}&travelmode=driving`;
};
